import type { CmmdMember, CmmdProject } from "@cmmd-forge/contracts";

/**
 * The project-level fields the Settings tab writes.
 *
 * Deliberately narrower than `UpdateProjectParams`: description lives in the
 * Markdown editor, and repo and work-item prefix keep their own inline
 * controls.
 *
 * `icon` is the emoji only. CMMD owns `iconUrl` — it is an S3 key written by
 * the repository icon sync, and no client can set it. Choosing an emoji clears
 * whatever image that sync had stored and stops it overwriting the choice.
 */
export interface CmmdProjectSettingsPatch {
  /** The board name of record. Renaming here renames the project everywhere. */
  readonly title?: string;
  /** The name Forge renders. `null` clears it and falls back to `title`. */
  readonly shortName?: string | null;
  readonly status?: string | null;
  readonly priority?: string | null;
  readonly ownerId?: string | null;
  readonly targetEndDate?: string | null;
  readonly icon?: string | null;
}

function ownerFields(
  ownerId: string | null,
  members: readonly CmmdMember[],
): Pick<CmmdProject, "ownerId" | "ownerName" | "ownerAvatarUrl"> {
  const owner = ownerId === null ? null : (members.find((member) => member.id === ownerId) ?? null);
  return {
    ownerId,
    ownerName: owner?.name ?? null,
    ownerAvatarUrl: owner?.avatarUrl ?? null,
  };
}

/**
 * Merge a saved settings patch into the cached project.
 *
 * `PATCH /projects/:id` answers with no body, so the cache has to be advanced
 * locally or the row keeps rendering the pre-save value until the next full
 * load. The lead needs more than a copy: the picker only sends `ownerId`, while
 * every surface reads `ownerName` / `ownerAvatarUrl`, so those are re-derived
 * from the member list rather than left pointing at the previous lead.
 */
export function applyProjectSettingsPatch(
  project: CmmdProject,
  patch: CmmdProjectSettingsPatch,
  members: readonly CmmdMember[],
): CmmdProject {
  return {
    ...project,
    ...(patch.title !== undefined ? { title: patch.title } : {}),
    // `null` is a real value here (clear the display name), so the guard has to
    // be on `undefined` — a truthiness check would drop the clear.
    ...(patch.shortName !== undefined ? { shortName: patch.shortName } : {}),
    ...(patch.status !== undefined ? { status: patch.status } : {}),
    ...(patch.priority !== undefined ? { priority: patch.priority } : {}),
    ...(patch.targetEndDate !== undefined ? { targetEndDate: patch.targetEndDate } : {}),
    // Setting an emoji clears the repo-synced image server-side, so the cached
    // project has to drop it too or the row keeps rendering the old image.
    ...(patch.icon !== undefined ? { icon: patch.icon, iconUrl: null } : {}),
    ...(patch.ownerId !== undefined ? ownerFields(patch.ownerId, members) : {}),
  };
}

/** Sentinel a settings picker uses for "no value": Base UI reads `null` as uncontrolled. */
export const PROJECT_SETTINGS_NONE_VALUE = "__none__";

type NullableSettingsField = "priority" | "ownerId";

/**
 * Resolve what a picker selection should save, or `null` for "save nothing".
 *
 * Every picker has the same three guards — the value is typed `unknown`, a
 * re-select of the current value must not PATCH, and the "none" sentinel means
 * clear rather than store a literal `__none__`. Writing them inline three times
 * is how one of the three ends up missing a guard.
 */
export function resolveSettingsSelection(
  field: NullableSettingsField,
  current: string,
  next: unknown,
): CmmdProjectSettingsPatch | null {
  if (typeof next !== "string" || next === current) return null;
  return { [field]: next === PROJECT_SETTINGS_NONE_VALUE ? null : next };
}

/** Status has no "none": CMMD always stores one, so a cleared value is not representable. */
export function resolveStatusSelection(
  current: string,
  next: unknown,
): CmmdProjectSettingsPatch | null {
  if (typeof next !== "string" || next === current) return null;
  return { status: next };
}
